import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { User, Users, Shield, FileText, Calendar, Bell, Sheet, Info, LogOut, MessageCircle, Menu, X, Eye } from 'lucide-react';
import ThemeToggle from '../ThemeToggle';
import PlayerInvite from '../PlayerInvite';

const WHATSAPP_COMMUNITY_LINK = "https://chat.whatsapp.com/GtvNjx1xhUb0dJIBXsEY76";

// Helper to get display role name
const getDisplayRole = (role) => {
  if (role === 'coach' || role === 'epp_staff') return 'EPP Staff';
  if (role === 'team_manager') return 'Team Manager';
  return role?.charAt(0)?.toUpperCase() + role?.slice(1) || '';
};

// Check if user has EPP Staff access
const isEppStaff = (role) => role === 'coach' || role === 'epp_staff' || role === 'admin';

// Check if user is Team Manager (view only)
const isTeamManager = (role) => role === 'team_manager';

export default function DashboardHeader({ 
  alertSummary, 
  onShowCoachBio, 
  onRefresh,
  mobileMenuOpen,
  setMobileMenuOpen 
}) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const userRole = user?.role;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="bg-zinc-950/80 backdrop-blur-xl shadow-2xl border-b border-zinc-800/50 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2 sm:gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_youth-athlete-lab/artifacts/pr7l13yi_Elite%20%284%29.png" 
              alt="BE ELITE APP" 
              className="h-10 sm:h-16"
            />
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-3">
            {/* User Info - Clickable for Coach Bio */}
            <button
              onClick={() => isEppStaff(userRole) && onShowCoachBio?.()}
              className={`flex items-center gap-3 px-4 py-2 bg-zinc-800/50 backdrop-blur rounded-full border border-zinc-700/50 ${isEppStaff(userRole) ? 'cursor-pointer hover:bg-zinc-700/50' : ''}`}
              title={isEppStaff(userRole) ? 'View/Edit Profile' : ''}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                isTeamManager(userRole) 
                  ? 'bg-gradient-to-br from-purple-500 to-purple-700' 
                  : 'bg-gradient-to-br from-[#02b5ff] to-[#ff025f]'
              }`}>
                {isTeamManager(userRole) ? <Eye className="h-4 w-4 text-white" /> : <User className="h-4 w-4 text-white" />}
              </div>
              <div className="text-sm text-left">
                <div className="font-semibold text-white" data-testid="user-name">{user?.full_name}</div>
                <div className={`text-xs ${isTeamManager(userRole) ? 'text-purple-400' : 'text-zinc-400'}`} data-testid="user-role">
                  {getDisplayRole(userRole)}
                  {isTeamManager(userRole) && ' (View Only)'}
                </div>
              </div>
            </button>
            
            <a
              href={WHATSAPP_COMMUNITY_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="h-10 w-10 bg-zinc-800/80 hover:bg-zinc-700/80 border border-zinc-700 hover:border-zinc-600 text-zinc-300 hover:text-white rounded-full transition-all flex items-center justify-center"
              data-testid="whatsapp-community-btn"
              title="WhatsApp Community"
            >
              <MessageCircle className="h-5 w-5" />
            </a>
            
            <ThemeToggle />
            
            {userRole === 'admin' && (
              <Button
                data-testid="admin-panel-button"
                onClick={() => navigate('/admin')}
                className="h-10 w-10 p-0 bg-zinc-800/80 hover:bg-zinc-700/80 border border-zinc-700 hover:border-zinc-600 text-zinc-300 hover:text-white rounded-full transition-all"
                title="Admin Panel"
              >
                <Shield className="h-5 w-5" />
              </Button>
            )}
            {(isEppStaff(userRole) || isTeamManager(userRole)) && (
              <>
                {/* EPP Staff only features */}
                {isEppStaff(userRole) && (
                  <>
                    <Button
                      data-testid="consent-forms-button"
                      onClick={() => navigate('/consent-forms')}
                      className="h-10 w-10 p-0 bg-zinc-800/80 hover:bg-zinc-700/80 border border-zinc-700 hover:border-zinc-600 text-zinc-300 hover:text-white rounded-full transition-all"
                      title="Consent Forms"
                    >
                      <FileText className="h-5 w-5" />
                    </Button>
                    <Button
                      data-testid="bookings-button"
                      onClick={() => navigate('/bookings')}
                      className="h-10 w-10 p-0 bg-zinc-800/80 hover:bg-zinc-700/80 border border-zinc-700 hover:border-zinc-600 text-zinc-300 hover:text-white rounded-full transition-all"
                      title="Bookings"
                    >
                      <Calendar className="h-5 w-5" />
                    </Button>
                  </>
                )}
                <Button
                  data-testid="coach-dashboard-button"
                  onClick={() => navigate(`/coach-dashboard?coach=${encodeURIComponent(user?.full_name || '')}`)}
                  className="h-10 w-10 p-0 bg-zinc-800/80 hover:bg-zinc-700/80 border border-zinc-700 hover:border-zinc-600 text-zinc-300 hover:text-white rounded-full transition-all"
                  title="My Athletes"
                >
                  <Users className="h-5 w-5" />
                </Button>
                {/* EPP Staff only features */}
                {isEppStaff(userRole) && (
                  <>
                    <Button
                      data-testid="google-sheets-button"
                      onClick={() => navigate('/settings/google-sheets')}
                      className="h-10 w-10 p-0 bg-zinc-800/80 hover:bg-zinc-700/80 border border-zinc-700 hover:border-zinc-600 text-zinc-300 hover:text-white rounded-full transition-all"
                      title="Google Sheets"
                    >
                      <Sheet className="h-5 w-5" />
                    </Button>
                    <Button
                      data-testid="services-info-button"
                      onClick={() => navigate('/services')}
                      className="h-10 w-10 p-0 bg-zinc-800/80 hover:bg-zinc-700/80 border border-zinc-700 hover:border-zinc-600 text-zinc-300 hover:text-white rounded-full transition-all"
                      title="Services"
                    >
                      <Info className="h-5 w-5" />
                    </Button>
                  </>
                )}
                {/* Alerts - next to Invite Player */}
                <Button
                  data-testid="alerts-button"
                  onClick={() => navigate('/alerts')}
                  className={`h-10 w-10 p-0 rounded-full transition-all relative ${
                    alertSummary?.requires_attention 
                      ? 'bg-[#ff025f]/20 hover:bg-[#ff025f]/30 border border-[#ff025f]/50 text-[#ff025f]' 
                      : 'bg-zinc-800/80 hover:bg-zinc-700/80 border border-zinc-700 hover:border-zinc-600 text-zinc-300 hover:text-white'
                  }`}
                  title="Alerts"
                >
                  <Bell className="h-5 w-5" />
                  {alertSummary?.unread > 0 && (
                    <span className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] text-xs font-bold rounded-full flex items-center justify-center text-white ${
                      alertSummary?.requires_attention ? 'bg-[#ff025f]' : 'bg-[#02b5ff]'
                    }`}>
                      {alertSummary.unread > 9 ? '9+' : alertSummary.unread}
                    </span>
                  )}
                </Button>
              </>
            )}
            {/* EPP Staff only - Player Invite */}
            {isEppStaff(userRole) && (
              <PlayerInvite teams={[]} onInviteSent={onRefresh} />
            )}
            <Button
              data-testid="logout-button"
              onClick={handleLogout}
              variant="ghost"
              className="h-10 w-10 p-0 text-zinc-400 hover:text-[#ff025f] hover:bg-[#ff025f]/10 rounded-full transition-all"
              title="Sign Out"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
          
          {/* Mobile Navigation */}
          <div className="flex lg:hidden items-center gap-2">
            <a
              href={WHATSAPP_COMMUNITY_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="h-10 w-10 bg-gradient-to-r from-[#02b5ff] to-[#0099dd] text-white rounded-full flex items-center justify-center shadow-lg shadow-[#02b5ff]/20"
            >
              <MessageCircle className="h-5 w-5" />
            </a>
            
            <Button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              variant="ghost"
              className="h-10 w-10 p-0 text-zinc-400 hover:text-white rounded-full"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-zinc-800 pt-4 space-y-2">
            <div className="flex items-center gap-3 px-4 py-3 bg-zinc-800/50 rounded-xl mb-4 border border-zinc-700/50">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isTeamManager(userRole) 
                  ? 'bg-gradient-to-br from-purple-500 to-purple-700' 
                  : 'bg-gradient-to-br from-[#02b5ff] to-[#ff025f]'
              }`}>
                {isTeamManager(userRole) ? <Eye className="h-5 w-5 text-white" /> : <User className="h-5 w-5 text-white" />}
              </div>
              <div>
                <div className="font-semibold text-white">{user?.full_name}</div>
                <div className={`text-xs ${isTeamManager(userRole) ? 'text-purple-400' : 'text-zinc-400'}`}>
                  {getDisplayRole(userRole)}
                  {isTeamManager(userRole) && ' (View Only)'}
                </div>
              </div>
            </div>
            
            {userRole === 'admin' && (
              <Button
                onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }}
                variant="ghost"
                className="w-full justify-start h-12 text-[#02b5ff] hover:bg-zinc-800 rounded-xl"
              >
                <Shield className="mr-3 h-5 w-5" />
                Admin Panel
              </Button>
            )}
            {(isEppStaff(userRole) || isTeamManager(userRole)) && (
              <>
                {isEppStaff(userRole) && (
                  <>
                    <Button
                      onClick={() => { navigate('/consent-forms'); setMobileMenuOpen(false); }}
                      variant="ghost"
                      className="w-full justify-start h-12 text-[#02b5ff] hover:bg-zinc-800 rounded-xl"
                    >
                      <FileText className="mr-3 h-5 w-5" />
                      Consent Forms
                    </Button>
                    <Button
                      onClick={() => { navigate('/bookings'); setMobileMenuOpen(false); }}
                      variant="ghost"
                      className="w-full justify-start h-12 text-[#02b5ff] hover:bg-zinc-800 rounded-xl"
                    >
                      <Calendar className="mr-3 h-5 w-5" />
                      Bookings
                    </Button>
                  </>
                )}
                <Button
                  onClick={() => { navigate(`/coach-dashboard?coach=${encodeURIComponent(user?.full_name || '')}`); setMobileMenuOpen(false); }}
                  variant="ghost"
                  className="w-full justify-start h-12 text-[#02b5ff] hover:bg-zinc-800 rounded-xl"
                >
                  <Users className="mr-3 h-5 w-5" />
                  My Athletes
                </Button>
              </>
            )}
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start h-12 text-[#ff025f] hover:bg-[#ff025f]/10 rounded-xl"
            >
              <LogOut className="mr-3 h-5 w-5" />
              Sign Out
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
